import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { queryClient } from "@/lib/query";

const AuthContext = createContext(null);

const ROLE_RANK = { agent: 0, manager: 1, admin: 2, owner: 3 };

export function AuthProvider({ children }) {
  // undefined = loading, null = anonymous, object = session
  const [session, setSession] = useState(undefined);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setSession(data);
      return data;
    } catch {
      setSession(null);
      return null;
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    const onUnauthorized = () => {
      setSession((prev) => (prev ? null : prev));
      queryClient.clear();
    };
    window.addEventListener("wappy:unauthorized", onUnauthorized);
    return () => window.removeEventListener("wappy:unauthorized", onUnauthorized);
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setSession(data);
    return data;
  };

  const signup = async (payload) => {
    const { data } = await api.post("/auth/signup", payload);
    setSession(data);
    return data;
  };

  const acceptInvite = async (payload) => {
    const { data } = await api.post("/auth/invitations/accept", payload);
    setSession(data);
    return data;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      /* ignore */
    }
    queryClient.clear();
    setSession(null);
  };

  const value = useMemo(() => {
    const user = session === undefined ? undefined : session?.user || null;
    const org = session?.organization || null;
    const role = user?.role || "agent";
    const can = (minimum) => (ROLE_RANK[role] ?? 0) >= (ROLE_RANK[minimum] ?? 0);
    return {
      session, user, org, role, can, login, signup, acceptInvite, logout, refresh,
      onboarding: session?.onboarding || null,
      roles: session?.roles || {},
      setSession,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
